import axios from 'axios';
import type { Department, FavoriteContact, OrgInfo, PaginationResult, User } from './types';

const BASE_URL = process.env.NEXT_PUBLIC_BACKEND_API || 'http://localhost:26293/api';
const DIRECTORY_URL = `${BASE_URL}/directory`;

const client = axios.create({
  baseURL: DIRECTORY_URL,
  headers: { 'Content-Type': 'application/json' },
});

// BE wraps responses in { code, message, data } — unwrap to get actual data
function unwrap<T>(response: { code?: number; message?: string; data?: T } | T): T {
  if (response && typeof response === 'object' && 'code' in response && 'data' in response) {
    return response.data as T;
  }
  return response as T;
}

export function setAuthToken(token?: string) {
  if (token) {
    client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete client.defaults.headers.common['Authorization'];
  }
}

export async function searchUsers(
  keyword?: string,
  departmentId?: string,
  limit = 12,
  offset = 0,
): Promise<PaginationResult<User>> {
  const { data } = await client.post('/search-users', {
    keyword,
    departmentId,
    options: { limit, offset },
  });
  return unwrap(data);
}

export async function getDepartmentTree(): Promise<PaginationResult<Department>> {
  const { data } = await client.get('/department-tree');
  return unwrap(data);
}

export async function getUsersByDepartment(
  departmentId: string,
  limit = 12,
  offset = 0,
): Promise<PaginationResult<User>> {
  const { data } = await client.post('/users-by-department', {
    departmentId,
    options: { limit, offset },
  });
  return unwrap(data);
}

export async function getUserProfile(userId: string): Promise<User> {
  const { data } = await client.get(`/user-profile/${userId}`);
  return unwrap(data);
}

export async function getUserOrgInfo(userId: string): Promise<OrgInfo> {
  const { data } = await client.get(`/user-org-info/${userId}`);
  return unwrap(data);
}

export async function getBirthdays(): Promise<User[]> {
  const { data } = await client.get('/birthdays');
  return unwrap(data);
}

export async function getNewEmployees(days = 30): Promise<User[]> {
  const { data } = await client.get(`/new-employees?days=${days}`);
  return unwrap(data);
}

export async function getFavorites(
  limit = 12,
  offset = 0,
): Promise<PaginationResult<FavoriteContact>> {
  const { data } = await client.post('/favorites/get-list', {
    options: { limit, offset },
  });
  return unwrap(data);
}

export async function addFavorite(favoriteUserId: string): Promise<FavoriteContact> {
  const { data } = await client.post('/favorites/add', { favoriteUserId });
  return unwrap(data);
}

export async function removeFavorite(favoriteUserId: string): Promise<void> {
  await client.post('/favorites/remove', { favoriteUserId });
}

export async function getCurrentUserProfile(session: {
  user?: { id?: string };
  accessToken?: string;
}): Promise<{ user: User; orgInfo: OrgInfo }> {
  if (!session?.user?.id) {
    throw new Error('User not authenticated');
  }

  if (session.accessToken) {
    setAuthToken(session.accessToken as string);
  }

  const [user, orgInfo] = await Promise.all([
    getUserProfile(session.user.id),
    getUserOrgInfo(session.user.id),
  ]);

  return { user, orgInfo };
}
